import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import UserLayout from '../../components/user/UserLayout';
import { Button } from '../../components/ui/button';
import { Camera, Plus, Trash2, Save, ArrowLeft } from 'lucide-react';
import { apiRequest } from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import CountryCodeSelector from '../../components/CountryCodeSelector';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

export default function UserDigitalCardConfigure() {
  const navigate = useNavigate();
  const { orderId } = useParams();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [checkingUsername, setCheckingUsername] = useState(false);
  const [usernameAvailable, setUsernameAvailable] = useState(null);
  const [usernameError, setUsernameError] = useState('');
  const [isUsernameSet, setIsUsernameSet] = useState(false);
  const [dynamicLinkCode, setDynamicLinkCode] = useState('');
  
  const [formData, setFormData] = useState({
    profile_photo: null,
    cover_photo: null,
    username: '',
    name: '',
    email: '',
    address: '',
    phone: '',
    company_name: '',
    designation: '',
    social_links: {
      facebook: '',
      twitter: '',
      instagram: '',
      linkedin: '',
      whatsapp: ''
    },
    whatsapp_country_code: '+1',
    custom_urls: []
  });
  
  const [profilePhotoPreview, setProfilePhotoPreview] = useState(null);
  const [coverPhotoPreview, setCoverPhotoPreview] = useState(null);

  useEffect(() => {
    if (!user) {
      navigate('/');
      return;
    }
    fetchConfiguration();
  }, [user, navigate, orderId]);

  useEffect(() => {
    // Fetch order details to get dynamic link code, but only set username if not already set
    if (!formData.username && !isUsernameSet) {
      fetchOrderDetails();
    }
  }, [formData.username, isUsernameSet]);

  const fetchOrderDetails = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await apiRequest(`${BACKEND_URL}/api/user/orders`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok && response.data.orders) {
        const order = response.data.orders.find(o => o.transaction_id === orderId);
        if (order && order.dynamic_link_details) {
          setDynamicLinkCode(order.dynamic_link_details.code);
          // Only set default username if not already set from configuration
          if (!formData.username && !isUsernameSet) {
            setFormData(prev => ({ ...prev, username: order.dynamic_link_details.code }));
          }
        }
      }
    } catch (err) {
      console.error('Failed to fetch order details:', err);
    }
  };

  const fetchConfiguration = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await apiRequest(`${BACKEND_URL}/api/user/digital-card-profile/${orderId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok && response.data) {
        const config = response.data;
        
        // Check if this is an actual profile (not just "No configuration found" message)
        if (config.order_id || config.username || config.name) {
          setFormData({
            profile_photo: null,
            cover_photo: null,
            username: config.username || '',
            name: config.name || '',
            email: config.email || '',
            address: config.address || '',
            phone: config.phone || '',
            company_name: config.company_name || '',
            designation: config.designation || '',
            social_links: config.social_links || {
              facebook: '',
              twitter: '',
              instagram: '',
              linkedin: '',
              whatsapp: ''
            },
            whatsapp_country_code: config.whatsapp_country_code || '+1',
            custom_urls: config.custom_urls || []
          });
          
          // If username exists in config, mark it as set
          if (config.username) {
            setIsUsernameSet(true);
          }
          
          if (config.profile_photo) {
            setProfilePhotoPreview(`${BACKEND_URL}${config.profile_photo}`);
          }
          if (config.cover_photo) {
            setCoverPhotoPreview(`${BACKEND_URL}${config.cover_photo}`);
          }
          
          console.log('Loaded existing configuration:', config);
        } else {
          console.log('No existing configuration found, using defaults');
        }
      }
    } catch (err) {
      console.error('Failed to fetch configuration:', err);
    }
  };

  const handlePhotoChange = (e, type) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        if (type === 'profile') {
          setProfilePhotoPreview(reader.result);
          setFormData({ ...formData, profile_photo: file });
        } else {
          setCoverPhotoPreview(reader.result);
          setFormData({ ...formData, cover_photo: file });
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const addCustomUrl = () => {
    setFormData({
      ...formData,
      custom_urls: [...formData.custom_urls, { name: '', url: '' }]
    });
  };

  const removeCustomUrl = (index) => {
    const newUrls = formData.custom_urls.filter((_, i) => i !== index);
    setFormData({ ...formData, custom_urls: newUrls });
  };

  const updateCustomUrl = (index, field, value) => {
    const newUrls = [...formData.custom_urls];
    newUrls[index][field] = value;
    setFormData({ ...formData, custom_urls: newUrls });
  };

  const checkUsernameAvailability = async (username) => {
    if (!username || username === dynamicLinkCode) {
      setUsernameAvailable(null);
      setUsernameError('');
      return;
    }

    setCheckingUsername(true);
    setUsernameError('');

    try {
      const token = localStorage.getItem('auth_token');
      const response = await apiRequest(`${BACKEND_URL}/api/user/check-username/${username}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        setUsernameAvailable(response.data.available);
        if (!response.data.available) {
          setUsernameError('Username is already taken');
        }
      }
    } catch (err) {
      setUsernameError('Failed to check username availability');
    } finally {
      setCheckingUsername(false);
    }
  };

  const handleUsernameBlur = () => {
    if (!isUsernameSet) {
      checkUsernameAvailability(formData.username);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('auth_token');
      const submitData = new FormData();
      
      // Append photos if changed
      if (formData.profile_photo instanceof File) {
        submitData.append('profile_photo', formData.profile_photo);
      }
      if (formData.cover_photo instanceof File) {
        submitData.append('cover_photo', formData.cover_photo);
      }
      
      // Append other fields
      submitData.append('username', formData.username);
      submitData.append('name', formData.name);
      submitData.append('email', formData.email);
      submitData.append('address', formData.address);
      submitData.append('phone', formData.phone);
      submitData.append('company_name', formData.company_name);
      submitData.append('designation', formData.designation);
      submitData.append('whatsapp_country_code', formData.whatsapp_country_code);
      submitData.append('social_links', JSON.stringify(formData.social_links));
      submitData.append('custom_urls', JSON.stringify(formData.custom_urls));

      const response = await apiRequest(`${BACKEND_URL}/api/user/digital-card-profile/${orderId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: submitData,
      });

      if (response.ok) {
        setSuccess('Profile configured successfully!');
        setTimeout(() => {
          navigate('/user/digital-cards');
        }, 2000);
      } else {
        throw new Error(response.data?.detail || 'Failed to save configuration');
      }
    } catch (err) {
      setError(err.message || 'Failed to save configuration');
    } finally {
      setLoading(false);
    }
  };

  return (
    <UserLayout>
      <div className="p-8">
        <div className="mb-6">
          <button
            onClick={() => navigate('/user/digital-cards')}
            className="flex items-center gap-2 text-gray-600 hover:text-navy mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Digital Cards
          </button>
          <h1 className="text-3xl font-extrabold text-navy">Configure Digital Card Profile</h1>
          <p className="text-gray-600 mt-2">Set up your digital card information and social links</p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}
        
        {success && (
          <div className="bg-green-50 border border-green-200 text-green-600 px-4 py-3 rounded-lg mb-6">
            {success}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Photos Section */}
          <div className="bg-white rounded-2xl p-6 shadow-sm">
            <h2 className="text-xl font-bold text-navy mb-4">Photos</h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              {/* Profile Photo */}
              <div>
                <label className="block text-sm font-semibold text-navy mb-2">Profile Photo</label>
                <div className="flex flex-col items-center">
                  <div className="w-40 h-40 rounded-full bg-gray-100 flex items-center justify-center overflow-hidden mb-4 border-4 border-gray-200">
                    {profilePhotoPreview ? (
                      <img src={profilePhotoPreview} alt="Profile" className="w-full h-full object-cover" />
                    ) : (
                      <Camera className="w-12 h-12 text-gray-400" />
                    )}
                  </div>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handlePhotoChange(e, 'profile')}
                    className="hidden"
                    id="profile-photo"
                  />
                  <label htmlFor="profile-photo" className="cursor-pointer px-4 py-2 bg-navy text-white rounded-lg hover:bg-navy/90">
                    Upload Photo
                  </label>
                </div>
              </div>

              {/* Cover Photo */}
              <div>
                <label className="block text-sm font-semibold text-navy mb-2">Cover Photo</label>
                <div className="flex flex-col items-center">
                  <div className="w-full h-40 rounded-lg bg-gray-100 flex items-center justify-center overflow-hidden mb-4 border-2 border-gray-200">
                    {coverPhotoPreview ? (
                      <img src={coverPhotoPreview} alt="Cover" className="w-full h-full object-cover" />
                    ) : (
                      <Camera className="w-12 h-12 text-gray-400" />
                    )}
                  </div>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handlePhotoChange(e, 'cover')}
                    className="hidden"
                    id="cover-photo"
                  />
                  <label htmlFor="cover-photo" className="cursor-pointer px-4 py-2 bg-navy text-white rounded-lg hover:bg-navy/90">
                    Upload Photo
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* Basic Information */}
          <div className="bg-white rounded-2xl p-6 shadow-sm">
            <h2 className="text-xl font-bold text-navy mb-4">Basic Information</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-navy mb-2">
                  Username {isUsernameSet && formData.username !== dynamicLinkCode && <span className="text-xs text-gray-500">(locked)</span>}
                </label>
                <input
                  type="text"
                  value={formData.username}
                  onChange={(e) => {
                    if (!isUsernameSet || formData.username === dynamicLinkCode) {
                      setFormData({ ...formData, username: e.target.value });
                      setUsernameAvailable(null);
                      setUsernameError('');
                    }
                  }}
                  onBlur={handleUsernameBlur}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-navy focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                  placeholder="johndoe"
                  disabled={isUsernameSet && formData.username !== dynamicLinkCode}
                />
                {checkingUsername && (
                  <p className="text-xs text-gray-500 mt-1">Checking availability...</p>
                )}
                {usernameError && (
                  <p className="text-xs text-red-600 mt-1">{usernameError}</p>
                )}
                {usernameAvailable === true && (!isUsernameSet || formData.username === dynamicLinkCode) && (
                  <p className="text-xs text-green-600 mt-1">✓ Username is available</p>
                )}
                {formData.username === dynamicLinkCode && (
                  <p className="text-xs text-blue-600 mt-1">💡 This is your default username. You can change it once.</p>
                )}
                {isUsernameSet && formData.username !== dynamicLinkCode && (
                  <p className="text-xs text-gray-500 mt-1">Username cannot be changed once customized</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-navy mb-2">Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-navy focus:border-transparent"
                  placeholder="John Doe"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-navy mb-2">Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-navy focus:border-transparent"
                  placeholder="john@example.com"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-navy mb-2">Phone</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-navy focus:border-transparent"
                  placeholder="+1 234 567 8900"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-navy mb-2">Company Name</label>
                <input
                  type="text"
                  value={formData.company_name}
                  onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-navy focus:border-transparent"
                  placeholder="Acme Inc."
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-navy mb-2">Designation</label>
                <input
                  type="text"
                  value={formData.designation}
                  onChange={(e) => setFormData({ ...formData, designation: e.target.value })}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-navy focus:border-transparent"
                  placeholder="CEO"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-navy mb-2">Address</label>
                <textarea
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-navy focus:border-transparent"
                  rows="3"
                  placeholder="123 Main St, City, Country"
                />
              </div>
            </div>
          </div>

          {/* Social Links */}
          <div className="bg-white rounded-2xl p-6 shadow-sm">
            <h2 className="text-xl font-bold text-navy mb-4">Social Links</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-navy mb-2">Facebook</label>
                <input
                  type="url"
                  value={formData.social_links.facebook}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    social_links: { ...formData.social_links, facebook: e.target.value } 
                  })}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-navy focus:border-transparent"
                  placeholder="https://facebook.com/username"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-navy mb-2">X (Twitter)</label>
                <input
                  type="url"
                  value={formData.social_links.twitter}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    social_links: { ...formData.social_links, twitter: e.target.value } 
                  })}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-navy focus:border-transparent"
                  placeholder="https://x.com/username"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-navy mb-2">Instagram</label>
                <input
                  type="url"
                  value={formData.social_links.instagram}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    social_links: { ...formData.social_links, instagram: e.target.value } 
                  })}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-navy focus:border-transparent"
                  placeholder="https://instagram.com/username"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-navy mb-2">LinkedIn</label>
                <input
                  type="url"
                  value={formData.social_links.linkedin}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    social_links: { ...formData.social_links, linkedin: e.target.value } 
                  })}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-navy focus:border-transparent"
                  placeholder="https://linkedin.com/in/username"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-navy mb-2">WhatsApp</label>
                <div className="flex gap-2">
                  <div className="w-64">
                    <CountryCodeSelector
                      value={formData.whatsapp_country_code}
                      onChange={(code) => setFormData({ ...formData, whatsapp_country_code: code })}
                    />
                  </div>
                  <input
                    type="tel"
                    value={formData.social_links.whatsapp}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      social_links: { ...formData.social_links, whatsapp: e.target.value } 
                    })}
                    className="flex-1 px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-navy focus:border-transparent"
                    placeholder="1234567890"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Custom URLs */}
          <div className="bg-white rounded-2xl p-6 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-navy">Custom URLs</h2>
              <Button
                type="button"
                onClick={addCustomUrl}
                className="bg-navy text-white hover:bg-navy/90"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add URL
              </Button>
            </div>

            <div className="space-y-4">
              {formData.custom_urls.map((url, index) => (
                <div key={index} className="flex gap-4 items-start">
                  <div className="flex-1">
                    <input
                      type="text"
                      value={url.name}
                      onChange={(e) => updateCustomUrl(index, 'name', e.target.value)}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-navy focus:border-transparent"
                      placeholder="Link Name (e.g., Portfolio)"
                    />
                  </div>
                  <div className="flex-1">
                    <input
                      type="url"
                      value={url.url}
                      onChange={(e) => updateCustomUrl(index, 'url', e.target.value)}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-navy focus:border-transparent"
                      placeholder="https://example.com"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => removeCustomUrl(index)}
                    className="p-3 text-red-600 hover:bg-red-50 rounded-lg"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              ))}

              {formData.custom_urls.length === 0 && (
                <p className="text-gray-500 text-center py-4">No custom URLs added yet</p>
              )}
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex gap-4">
            <Button
              type="submit"
              disabled={loading}
              className="flex-1 bg-navy text-white hover:bg-navy/90 py-6 text-lg"
            >
              {loading ? (
                'Saving...'
              ) : (
                <>
                  <Save className="w-5 h-5 mr-2" />
                  Save Configuration
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </UserLayout>
  );
}
